import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type ApiKey, type ApiKeyCreated } from '../api/client';
import { ConfirmDialog } from '../components/ConfirmDialog';

export function ApiKeys() {
  const { currentWorkspaceId: workspaceId } = useAuth();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyTags, setNewKeyTags] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  // Tag editing state
  const [editingTagsKeyId, setEditingTagsKeyId] = useState<string | null>(null);
  const [editTagsValue, setEditTagsValue] = useState('');

  async function loadKeys() {
    if (!workspaceId) return;
    try {
      const data = await api.getApiKeys(workspaceId);
      setKeys(data.api_keys);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load API keys.',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadKeys();
  }, [workspaceId]);

  function parseTags(input: string): string[] {
    return input
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  }

  async function handleCreate() {
    if (!workspaceId || !newKeyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const tags = parseTags(newKeyTags);
      const key = await api.createApiKey(workspaceId, {
        name: newKeyName.trim(),
        tags: tags.length > 0 ? tags : undefined,
      });
      setCreatedKey(key);
      setNewKeyName('');
      setNewKeyTags('');
      await loadKeys();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create API key.',
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(keyId: string) {
    if (!workspaceId) return;
    setError(null);
    try {
      await api.revokeApiKey(workspaceId, keyId);
      await loadKeys();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to revoke API key.',
      );
    }
  }

  async function handleSaveTags(keyId: string) {
    if (!workspaceId) return;
    setError(null);
    try {
      await api.updateApiKey(workspaceId, keyId, {
        tags: parseTags(editTagsValue),
      });
      setEditingTagsKeyId(null);
      await loadKeys();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update tags.',
      );
    }
  }

  async function handleCopy() {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!workspaceId) {
    return (
      <div className="rounded-xl border border-border-dark bg-surface-1 p-8 text-center text-sm text-text-secondary">
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">API Keys</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Manage API keys for authenticating agents with the gateway
          </p>
        </div>
        <button
          onClick={() => {
            setShowCreate(!showCreate);
            setCreatedKey(null);
          }}
          className="rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:opacity-90"
        >
          Create API Key
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {/* Newly created key banner */}
      {createdKey && (
        <div className="mb-6 rounded-xl border border-[rgba(52,211,153,0.2)] bg-[rgba(52,211,153,0.12)] p-4">
          <p className="text-sm font-medium text-[var(--color-success)]">
            API key created successfully
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg bg-surface-2 px-3 py-2 text-xs text-text-primary">
              {createdKey.key}
            </code>
            <button
              onClick={handleCopy}
              className="rounded-md bg-[rgba(52,211,153,0.2)] px-3 py-2 text-xs font-medium text-[var(--color-success)] transition-colors hover:bg-[rgba(52,211,153,0.3)]"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="mt-2 text-xs text-[var(--color-warning)]">
            Copy this key now. It will not be shown again.
          </p>
        </div>
      )}

      {/* Create form */}
      {showCreate && !createdKey && (
        <div className="mb-6 rounded-xl border border-border-dark bg-surface-1 p-6">
          <h3 className="text-sm font-semibold text-text-primary">
            Create new API key
          </h3>
          <div className="mt-4 space-y-3">
            <div>
              <label
                htmlFor="keyName"
                className="block text-sm font-medium text-text-primary"
              >
                Key name
              </label>
              <input
                id="keyName"
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., production-agent"
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
              />
            </div>
            <div>
              <label
                htmlFor="keyTags"
                className="block text-sm font-medium text-text-primary"
              >
                Tags <span className="font-normal text-text-tertiary">(comma-separated, optional)</span>
              </label>
              <input
                id="keyTags"
                type="text"
                value={newKeyTags}
                onChange={(e) => setNewKeyTags(e.target.value)}
                placeholder="e.g., project:demo, env:staging, team:backend"
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={handleCreate}
                disabled={creating || !newKeyName.trim()}
                className="rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-lg border border-border-dark px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-surface-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keys table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 spinner-gradient" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border-dark bg-surface-1">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border-dark bg-surface-0">
                <th className="px-4 py-3 font-medium text-text-secondary">
                  Prefix
                </th>
                <th className="px-4 py-3 font-medium text-text-secondary">Name</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Roles</th>
                <th className="px-4 py-3 font-medium text-text-secondary">Tags</th>
                <th className="px-4 py-3 font-medium text-text-secondary">
                  Created
                </th>
                <th className="px-4 py-3 font-medium text-text-secondary">
                  Status
                </th>
                <th className="px-4 py-3 font-medium text-text-secondary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-text-secondary"
                  >
                    No API keys found. Create one to get started.
                  </td>
                </tr>
              ) : (
                keys.map((key) => (
                  <tr
                    key={key.id}
                    className="border-b border-border-subtle last:border-0"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                      {key.prefix}...
                    </td>
                    <td className="px-4 py-3 text-text-primary">{key.name}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {key.roles.map((role) => (
                          <span
                            key={role}
                            className="inline-flex rounded-full bg-[rgba(6,182,164,0.12)] px-2 py-0.5 text-xs font-medium text-accent-teal"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {editingTagsKeyId === key.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editTagsValue}
                            onChange={(e) => setEditTagsValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveTags(key.id);
                              if (e.key === 'Escape') setEditingTagsKeyId(null);
                            }}
                            className="w-40 rounded border border-border-dark bg-surface-2 px-2 py-0.5 text-xs text-text-primary focus:border-accent-teal focus:outline-none"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveTags(key.id)}
                            className="text-xs font-medium text-accent-teal hover:opacity-80"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingTagsKeyId(null)}
                            className="text-xs font-medium text-text-tertiary hover:opacity-80"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div
                          className="flex flex-wrap items-center gap-1 cursor-pointer group"
                          onClick={() => {
                            if (!key.revoked) {
                              setEditingTagsKeyId(key.id);
                              setEditTagsValue((key.tags || []).join(', '));
                            }
                          }}
                        >
                          {(key.tags || []).length > 0 ? (
                            key.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex rounded-full bg-[rgba(139,92,246,0.12)] px-2 py-0.5 text-xs font-medium text-[var(--accent-violet)]"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-text-tertiary group-hover:text-text-secondary">
                              {key.revoked ? '--' : '+ add tags'}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">
                      {new Date(key.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {key.revoked ? (
                        <span className="inline-flex rounded-full bg-[rgba(248,113,113,0.12)] px-2 py-0.5 text-xs font-medium text-[var(--color-error)]">
                          Revoked
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-[rgba(52,211,153,0.12)] px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!key.revoked && (
                        <button
                          onClick={() => setRevokeTarget(key.id)}
                          className="text-xs font-medium text-[var(--color-error)] transition-colors hover:opacity-80"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke API Key"
        message="This API key will be permanently revoked. Any agents using it will lose access immediately. This action cannot be undone."
        confirmLabel="Revoke Key"
        confirmVariant="danger"
        onConfirm={() => {
          if (revokeTarget) handleRevoke(revokeTarget);
          setRevokeTarget(null);
        }}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
